




import { Component, Emit, Inject, Prop, Vue } from 'vue-property-decorator';
import { Destroyed, Mounted } from '../../decorators';
import { combineLatest, interval, merge, NEVER, Subject } from 'rxjs';
import { log, pluckAndStartWatch, queryState } from '../../operators';
import { ColorsList, customPinElement, customSvgForMarker, hasElements, PlaybackState, VisualizationInfo } from '../../utils';
import { filter, map, scan, share, startWith, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { createInfo, getUnselectedLines, mouseoutEvent, mouseoverEvent } from './index';
import { Marker } from '../../models';
import { getDefaultMarker } from '../../../tracking/map-markers';
import { SvgTrackingIcons, SvgTrackingIconsType } from '../../../tracking/icons';
import { attachMarkerListeners, getInfoForFilters, getInfoWindow } from '../../../tracking/map-markers-infos';

const mapState = (state: string) => {
    switch (state) {
        case PlaybackState.STOP:
            return { ...playBackInitialState, ...positionInitialState, undefined: false, stop: true };
        case PlaybackState.PAUSE:
            return { ...playBackInitialState, undefined: false, pause: true };
        case PlaybackState.PLAY:
            return { ...playBackInitialState, undefined: false, play: true };
        default:
            return { ...playBackInitialState, ...positionInitialState };
    }
};

enum playBackStateKeys {
    PLAY = 'play',
    STOP = 'stop',
    PAUSE = 'pause',
    UNDEFINED = 'undefined',
    POSITION = 'position',
    SPEED = 'speed',
    VISUALIZATION_APPLY = 'visualizationApply',
    MARKERS_TO_PAINT = 'markersToPaint'
}

const playBackInitialState = {
    play: false,
    stop: false,
    pause: false,
    undefined: true
};

const positionInitialState = {
    position: -1
};

const initialState = {
    ...playBackInitialState,
    ...positionInitialState,
    speed: 50,
    visualizationApply: { markers: [], start: 0, end: -1 },
    markersToPaint: []
};

@Component
export default class DrawVisualizations extends Vue {
    @Prop() speed: number;
    @Prop() state: string;
    @Prop() visualizationApply: VisualizationInfo;
    @Inject() mapApi: google.maps.Map;
    unSubscribe$: Subject<void> = new Subject<void>();
    dispatch$: Subject<any> = new Subject<any>();
    polygons: google.maps.Polygon[] = [];
    drawFirstUnselectedPath: google.maps.Polyline | null = null;
    drawSecondUnselectedPath: google.maps.Polyline | null = null;
    firstFlag: google.maps.marker.AdvancedMarkerElement | null = null;
    firstFlagInfoWindows: google.maps.InfoWindow | null = null;
    secondFlag: google.maps.marker.AdvancedMarkerElement | null = null;
    secondFlagInfoWindows: google.maps.InfoWindow | null = null;
    color: string = '#FF0000';
    polygonsInfoWindows: google.maps.InfoWindow[] = [];

    @Mounted
    onMounted(): void {
        // == SOURCE OBSERVABLES ==================================================

        const speed$ = this.$watchAsObservable('speed').pipe(pluckAndStartWatch(initialState.speed));
        const state$ = this.$watchAsObservable('state').pipe(pluckAndStartWatch(PlaybackState.UNDEFINED));
        const visualizationApply$ = this.$watchAsObservable('visualizationApply').pipe(
            pluckAndStartWatch({ markers: [], start: 0, end: -1 })
        );

        // === STATE OBSERVABLES ==================================================

        const playBackCommands$ = merge(
            speed$.pipe(map(speed => ({ speed }))),
            state$.pipe(map(mapState)),
            visualizationApply$.pipe(
                map(visualizationApply => ({
                    visualizationApply,
                    markersToPaint: this.extractFragment(visualizationApply)
                }))
            ),
            this.dispatch$.asObservable()
        );

        const playBackState$ = playBackCommands$.pipe(
            startWith(initialState),
            scan((playBackState, command) => ({ ...playBackState, ...command })),
            share()
        );

        // == INTERMEDIATE OBSERVABLES ============================================

        const isPlay$ = playBackState$.pipe(queryState(playBackStateKeys.PLAY));

        const isUndefined$ = playBackState$.pipe(queryState(playBackStateKeys.UNDEFINED));

        const isStop$ = playBackState$.pipe(queryState(playBackStateKeys.STOP));

        const intervalSpeed$ = playBackState$.pipe(queryState(playBackStateKeys.SPEED));

        const position$ = playBackState$.pipe(queryState(playBackStateKeys.POSITION));

        const visualization$ = playBackState$.pipe(queryState(playBackStateKeys.VISUALIZATION_APPLY));

        const intervalPlay$ = combineLatest([isPlay$, intervalSpeed$]).pipe(
            switchMap(([isPlay, speed]) => (isPlay ? interval(speed as number) : NEVER))
        );

        const markersToPaint$ = playBackState$.pipe(queryState(playBackStateKeys.MARKERS_TO_PAINT));

        const firstPosition$ = position$.pipe(filter(position => position === 0));

        const isLastPosition$ = position$.pipe(
            withLatestFrom(markersToPaint$),
            map(([position, markersToPaint]) => this.isIntervalFinishToPaint(position, markersToPaint))
        );

        // = SIDE EFFECTS =========================================================

        const updatePosition$ = intervalPlay$.pipe(
            withLatestFrom(position$, (_, position) => position),
            tap(position => this.dispatch$.next({ position: position + 1 }))
        );

        const emitUndefinedState$ = isStop$.pipe(
            filter(isStop => isStop),
            tap(() => this.setUndefinedState())
        );

        const cleanFlags$ = visualization$.pipe(
            tap(visualization => {
                if (!this.isValidVisualizationInfo(visualization)) {
                    this.clean();
                }
            })
        );

        //draw complete visualization
        const drawVisualization$ = combineLatest([isUndefined$, visualization$]).pipe(
            filter<[boolean, VisualizationInfo]>(
                ([isUndefined, visualization]) =>
                    isUndefined && hasElements(visualization.markers) && this.isValidVisualizationInfo(visualization)
            ),
            map(([_, visualization]) => visualization),
            withLatestFrom(markersToPaint$),
            tap(([visualization, markersToPaint]) =>
                this.drawCompleteVisualization(visualization, markersToPaint, this.color)
            )
        );

        //draw marker in some position
        const drawVisualizationInPosition$ = position$.pipe(
            withLatestFrom(visualization$, isLastPosition$),
            filter<[number, VisualizationInfo, boolean]>(
                ([position, visualization, lastPosition]) =>
                    position >= 0 &&
                    hasElements(visualization.markers) &&
                    !lastPosition &&
                    this.isValidVisualizationInfo(visualization)
            ),
            map(([position, _]) => position),
            withLatestFrom(markersToPaint$),
            tap(([position, markersToPaint]) => this.drawVisualizationInPosition(position, markersToPaint))
        );

        const cleanWhenIntervalStart$ = firstPosition$.pipe(tap(() => this.clean()));

        const finishInterval$ = isLastPosition$.pipe(
            filter(lastPosition => lastPosition),
            tap(() => this.finishInterval())
        );

        // == SUBSCRIPTION ========================================================

        const updateStateTrigger$ = merge(
            updatePosition$,
            emitUndefinedState$,
            drawVisualization$,
            cleanWhenIntervalStart$,
            drawVisualizationInPosition$,
            finishInterval$,
            cleanFlags$
        )
            .pipe(takeUntil(this.unSubscribe$))
            .subscribe();

        this.dispatch$.next({
            ...mapState(this.state),
            speed: this.speed,
            visualizationApply: this.visualizationApply,
            markersToPaint: this.extractFragment(this.visualizationApply)
        });
    }

    @Emit()
    setUndefinedState() {}

    drawUnselectedLines(visualization: VisualizationInfo, color: string) {
        const unselectedLines = getUnselectedLines(
            visualization.markers,
            visualization.start,
            visualization.end,
            color
        );
        this.drawFirstUnselectedPath = unselectedLines[0];
        this.drawFirstUnselectedPath.setMap(this.mapApi);
        this.drawSecondUnselectedPath = unselectedLines[1];
        this.drawSecondUnselectedPath.setMap(this.mapApi);
    }

    drawCompleteVisualization(visualization: VisualizationInfo, markersToPaint: Marker[], color: string): void {
        this.clean();
        this.drawUnselectedLines(visualization, color);
        this.drawFlags(markersToPaint);
        markersToPaint.forEach(this.buildAndAddRectangle);
    }

    drawVisualizationInPosition(position: number, markersToPaint: Marker[]): void {
        this.drawFlagsInInterval(position, markersToPaint);
        this.drawInPosition(position, markersToPaint);
    }

    drawOnlyVisualizationInPosition(position: number, markersToPaint: Marker[]): void {
        this.clean();
        this.drawInPosition(position, markersToPaint);
    }

    drawInPosition(position: number, markersToPaint: Marker[]): void {
        const rectangle = this.createRectangle(markersToPaint[position]);
        const rectangleBounds = rectangle.getBounds() as google.maps.LatLngBounds;
        const polygon: google.maps.Polygon = this.createPolygonFromRectangle(rectangle);
        this.rotatePolygon(polygon, rectangleBounds.getCenter(), this.getRotation(markersToPaint[position]));
        const polygonInfoWindows: google.maps.InfoWindow = createInfo(
            markersToPaint[position],
            rectangleBounds.getCenter()
        );
        this.openWindowsMarker(polygon, polygonInfoWindows);
        this.closeWindowsMarker(polygon, polygonInfoWindows);
        this.polygonsInfoWindows.push(polygonInfoWindows);
        this.polygons.push(polygon);
    }

    drawFlagsInInterval(position: number, markersToPaint: Marker[]): void {
        if (position === 0) {
            this.drawFirstFlag(this.getLatLng(markersToPaint[position]));
        } else if (position === markersToPaint.length - 1) {
            this.drawSecondFlag(this.getLatLng(markersToPaint[position]));
        }
    }

    drawFlags(markersToPaint: Marker[]): void {
        this.drawFirstFlag(this.getLatLng(markersToPaint[0]));
        this.drawSecondFlag(this.getLatLng(markersToPaint[markersToPaint.length - 1]));
    }

    drawFirstFlag(position: google.maps.LatLng): void {
        this.firstFlag = this.createFlagMarker(position, SvgTrackingIcons.GreenFlag);
        this.firstFlagInfoWindows = this.createFlagInfoWindows('Inicio');
        attachMarkerListeners(this.firstFlag, this.firstFlagInfoWindows)
    }

    drawSecondFlag(position: google.maps.LatLng): void {
        this.secondFlag = this.createFlagMarker(position, SvgTrackingIcons.RedFlag);
        this.secondFlagInfoWindows = this.createFlagInfoWindows('Fin');
        attachMarkerListeners(this.secondFlag, this.secondFlagInfoWindows)
    }


    isIntervalFinishToPaint(position: number, markersToPaint: Marker[]): boolean {
        return position > markersToPaint.length - 1;
    }

    finishInterval(): void {
        this.setUndefinedState();
    }

    createRectangle(marker: Marker): google.maps.Rectangle {
        let bounds = this.createBounds(this.getLatLng(marker), this.getRadius(marker));
        let color = this.getColor(marker);
        return new google.maps.Rectangle({
            strokeColor: color,
            strokeOpacity: 0.5,
            strokeWeight: 0.5,
            fillColor: color,
            fillOpacity: 0.5,
            bounds
        });
    }

    createPolygonFromRectangle(rectangle: google.maps.Rectangle): google.maps.Polygon {
        const rectangleBounds = rectangle.getBounds() as google.maps.LatLngBounds;
        const paths: google.maps.LatLngLiteral[] = [
            {
                lat: rectangleBounds.getNorthEast().lat(),
                lng: rectangleBounds.getNorthEast().lng()
            },
            {
                lat: rectangleBounds.getNorthEast().lat(),
                lng: rectangleBounds.getSouthWest().lng()
            },
            {
                lat: rectangleBounds.getSouthWest().lat(),
                lng: rectangleBounds.getSouthWest().lng()
            },
            {
                lat: rectangleBounds.getSouthWest().lat(),
                lng: rectangleBounds.getNorthEast().lng()
            }
        ];
        const polygon = new google.maps.Polygon({
            paths,
            zIndex: 21
        });
        const properties = ['strokeColor', 'strokeOpacity', 'strokeWeight', 'fillOpacity', 'fillColor'];
        const options = {};
        properties.forEach(property => {
            // eslint-disable-next-line no-prototype-builtins
            if (rectangle.hasOwnProperty(property)) {
                options[property] = rectangle[property];
            }
        });
        polygon.setOptions(options);
        polygon.setMap(this.mapApi);
        return polygon;
    }

    rotatePolygon(polygon: google.maps.Polygon, center: google.maps.LatLng, angle: number) {
        const prj = this.mapApi.getProjection() as google.maps.Projection;
        const origin: google.maps.Point = prj.fromLatLngToPoint(center) as google.maps.Point;
        const coords = polygon
            .getPath()
            .getArray()
            .map(latLng => {
                const point = prj.fromLatLngToPoint(latLng) as google.maps.Point;
                const rotatedLatLng = prj.fromPointToLatLng(this.rotatePoint(point, origin, angle)) as google.maps.LatLng;
                return { lat: rotatedLatLng.lat(), lng: rotatedLatLng.lng() };
            });
        polygon.setPath(coords);
    }

    rotatePoint(point: google.maps.Point, origin: google.maps.Point, angle: number): google.maps.Point {
        const angleRad = (angle * Math.PI) / 180.0;
        const x = Math.cos(angleRad) * (point.x - origin.x) - Math.sin(angleRad) * (point.y - origin.y) + origin.x;
        const y = Math.sin(angleRad) * (point.x - origin.x) + Math.cos(angleRad) * (point.y - origin.y) + origin.y;
        return new google.maps.Point(x, y);
    }

    createBounds(latLng: google.maps.LatLng, radius: number): google.maps.LatLngBounds {
        return new google.maps.Circle({
            center: latLng,
            radius: radius
        }).getBounds() as google.maps.LatLngBounds;
    }

    createFlagMarker(position: google.maps.LatLng, flagSvgIcon: string): google.maps.marker.AdvancedMarkerElement {
        let flagMarker = getDefaultMarker({
            lat: position.lat(),
            lng: position.lng()
        });
        flagMarker.content = customSvgForMarker(flagSvgIcon)
        flagMarker.map = this.mapApi;
        return flagMarker;
    }

    createFlagInfoWindows(message: string) {
        return getInfoWindow(this.getContent(message))
    }

    getContent(message: string): string {
        return '<div id="content">' + `<span class='black-description'>${message}</span>` + `</div>`;
    }

    getRadius(marker: Marker): number {
        return marker && marker.infoState && marker.infoState.metters ? marker.infoState.metters / 2 : 5;
    }

    getLatLng(marker: Marker): google.maps.LatLng {
        return new google.maps.LatLng(marker.lat, marker.lng);
    }

    getColor(marker: Marker): string {
        return marker && marker.infoState && marker.infoState.color ? marker.infoState.color : '';
    }

    getRotation(marker: Marker): number {
        return marker.icon.rotation;
    }

    hidePolygons(): void {
        this.polygons.forEach(rectangle => rectangle.setMap(null));
    }

    clearPolygonsInfoWindows(): void {
        this.polygonsInfoWindows = [];
    }

    clearPolygons(): void {
        this.polygons = [];
    }

    extractFragment(visualization: VisualizationInfo): Marker[] {
        return visualization.start < visualization.end
            ? visualization.markers.slice(visualization.start, visualization.end)
            : [];
    }

    buildAndAddRectangle(marker: Marker): void {
        const rectangle: google.maps.Rectangle = this.createRectangle(marker);
        const polygon: google.maps.Polygon = this.createPolygonFromRectangle(rectangle);
        const rectangleBounds = rectangle.getBounds() as google.maps.LatLngBounds;
        this.rotatePolygon(polygon, rectangleBounds.getCenter(), this.getRotation(marker));
        // console.log("buildAndAddRectangle -> create info filters")
        const polygonInfoWindows: google.maps.InfoWindow = getInfoForFilters(marker, rectangleBounds.getCenter());
        this.openWindowsMarker(polygon, polygonInfoWindows);
        this.closeWindowsMarker(polygon, polygonInfoWindows);
        this.polygonsInfoWindows.push(polygonInfoWindows);
        this.polygons.push(polygon);
    }

    openWindowsMarker(polygon: google.maps.Polygon, rectangleInfoWindow: google.maps.InfoWindow): void {
        google.maps.event.addListener(polygon, mouseoverEvent, () => {
            rectangleInfoWindow.open(this.mapApi, polygon);
        });
    }

    closeWindowsMarker(polygon: google.maps.Polygon, rectangleInfoWindow: google.maps.InfoWindow): void {
        google.maps.event.addListener(polygon, mouseoutEvent, () => {
            rectangleInfoWindow.close();
        });
    }

    isValidVisualizationInfo(visualization: VisualizationInfo): boolean {
        return visualization.start < visualization.end;
    }

    clearUnselectedLines(): void {
        if (this.drawFirstUnselectedPath !== null) {
            this.drawFirstUnselectedPath.setMap(null);
            this.drawFirstUnselectedPath = null;
        }
        if (this.drawSecondUnselectedPath !== null) {
            this.drawSecondUnselectedPath.setMap(null);
            this.drawSecondUnselectedPath = null;
        }
    }

    clearFlags(): void {
        if (this.firstFlag !== null) {
            this.firstFlag.map = null;
            this.firstFlag = null;
        }
        if (this.secondFlag !== null) {
            this.secondFlag.map = null
            this.secondFlag = null;
        }
    }

    clearFlagsInfoWindows(): void {
        if (this.firstFlagInfoWindows !== null) {
            this.firstFlagInfoWindows = null;
        }
        if (this.secondFlagInfoWindows !== null) {
            this.secondFlagInfoWindows = null;
        }
    }

    @Destroyed
    clean(): void {
        this.hidePolygons();
        this.clearPolygons();
        this.clearUnselectedLines();
        this.clearFlags();
        this.clearPolygonsInfoWindows();
        this.clearFlagsInfoWindows();
    }

    @Destroyed
    unSubscribe(): void {
        this.unSubscribe$.next();
        this.unSubscribe$.complete();
    }
}
